import { useCallback, useEffect, useState } from 'react';
import { autorun, toJS } from 'mobx';
import { NavListProps, NavSectionProps } from 'src/components/nav-section';
import { useStores } from 'src/models';
import { IMenuSnapshot } from 'src/models/user/User';
import { MENU_TYPE_CODE_TO_ENG } from 'src/static/constants';

function useNavData() {
  const { commonCodeStore, authStore } = useStores();
  const MENU_TYPES = commonCodeStore.getCCodeByName('MENU_TYPE_CD');
  const [menus, setMenus] = useState<Array<IMenuSnapshot>>([]);
  const [navData, setNavData] = useState<NavSectionProps['data']>([]);

  useEffect(() => {
    const disposer = autorun(() => {
      if (authStore.user) {
        setMenus(toJS(authStore.user.menuList));
      }
    });

    return () => disposer();
  }, [authStore.user]);

  const getNavData = useCallback(async () => {
    if (menus.length === 0) return;

    // 하위 메뉴를 재귀적으로 추가하는 함수
    const appendChildrenMenu = (parentSid: number | null): NavListProps[] => {
      const children = menus.filter((menu) => menu.menuPsid === parentSid);
      return children
        .sort((a, b) => a.ordr - b.ordr)
        .map((child) => ({
          title: child.menuNm,
          path: child.menuUri,
          opened: true,
          children: appendChildrenMenu(child.menuSid), // 재귀 호출로 하위 메뉴 추가
          activePaths: child.menuUri ? [child.menuUri] : [],
        }));
    };

    // 메뉴 타입의 하위 섹션 이름 가져오기
    const getSubheader = (menuType: { code: number }): string => {
      const subheader = (MENU_TYPE_CODE_TO_ENG as typeof MENU_TYPE_CODE_TO_ENG)[
        menuType.code as keyof typeof MENU_TYPE_CODE_TO_ENG
      ];
      return subheader ? subheader.toUpperCase() : '';
    };

    // 메뉴 데이터를 그룹화
    const data: NavSectionProps['data'] = (MENU_TYPES || []).map((menuType) => {
      const items = menus
        .filter((menu) => menu.menuTypeCd?.code === menuType.value && menu.menuPsid === null)
        .sort((a, b) => a.ordr - b.ordr)
        .map((menu) => ({
          title: menu.menuNm,
          path: menu.menuUri,
          opened: true,
          children: appendChildrenMenu(menu.menuSid), // 하위 메뉴 추가
          activePaths: menu.menuUri ? [menu.menuUri] : [],
        })) as NavListProps[];
      return {
        subheader: getSubheader({ code: menuType.value }),
        items,
      };
    });

    // 빈 섹션 필터링
    const newNavData = data.filter((section) => section.items.length > 0);

    setNavData(newNavData);
  }, [menus]);

  useEffect(() => {
    getNavData();
  }, [getNavData]);

  return navData;
}

export default useNavData;
